/** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react';
import { css, keyframes, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../constants.tsx';
import { PopupContent } from '../popup-content/index.tsx';
import { checkIsClamped, getClosestTextElement } from '../../controllers/utils.tsx';
import type { PopoverInternalProps } from './types.tsx';

const CELL_HOVER_DELAY = 200;

export const PopoverInternal = ({
	content,
	contentRef,
	entryPointActions,
	isInteractive,
	shouldGetParentRef,
	isFixedWidth,
	hidePopover,
	analyticsAttributes,
	onUpdate,
	isReferenceHidden,
}: PopoverInternalProps) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		entryPointActions?.preload();

		hoverTimeoutRef.current = setTimeout(() => {
			const elementWithTextContent = contentRef?.current
				? getClosestTextElement(
						shouldGetParentRef ? contentRef?.current.parentElement : contentRef?.current,
					)
				: undefined;

			const clientWidth = elementWithTextContent?.clientWidth || 0;
			const scrollWidth = elementWithTextContent?.scrollWidth || 0;
			const isClamped = checkIsClamped(elementWithTextContent);

			if (!contentRef?.current || scrollWidth > clientWidth || isClamped) {
				entryPointActions?.load();
				setIsPopupOpen(true);
			}
		}, CELL_HOVER_DELAY);

		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, [contentRef, shouldGetParentRef, setIsPopupOpen, entryPointActions]);

	if (isPopupOpen && content && !hidePopover && !isReferenceHidden) {
		return (
			<UFOSegment name="jira-list-hover-popover" mode="list">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
					<div
						css={[
							containerStyles,
							isInteractive && interactiveStyles,
							isFixedWidth && fixedWidthStyles,
						]}
						onMouseEnter={() => isInteractive && setIsPopupOpen(true)}
						onMouseLeave={() => isInteractive && setIsPopupOpen(false)}
					>
						<PopupContent
							isInteractive={isInteractive}
							update={onUpdate}
							analyticsAttributes={analyticsAttributes}
						>
							{content}
						</PopupContent>
					</div>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const interactiveStyles = css({
	marginBlock: token('space.negative.100'),
	paddingBlock: token('space.150'),
	pointerEvents: 'auto',
});

const containerStyles = css({
	marginBlock: token('space.100'),
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});

const fixedWidthStyles = css({
	width: '300px',
});
